import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Award, Zap, Check, Building, Briefcase, GraduationCap, Compass, BookOpen, Medal } from 'lucide-react';

const HomePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-badge">Professional Sober Coaching Services</div>
        <h1>Transform Your Life,<br />Embrace <span className="gold">Sobriety</span></h1>
        <p className="hero-subtitle">
          Welcome to Dr. Drea Sober Coaching. With experience as a drug, alcohol, and 
          mental health counselor, I provide personalized guidance to support your recovery journey.
        </p>
        <div className="hero-buttons">
          <Link to="/packages" className="btn-primary" data-testid="view-packages-btn">
            View Coaching Packages <ArrowRight size={18} />
          </Link>
          <Link to="/resources" className="btn-secondary" data-testid="resources-btn">
            Recovery Resources
          </Link>
        </div>
      </section>

      {/* Journey Section */}
      <section className="journey-section">
        <div className="journey-content">
          <div className="journey-image-container">
            <div className="journey-image">
              <img 
                src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop" 
                alt="Mountain sunrise representing new beginnings" 
              />
            </div>
            <div className="journey-badge">Your Journey Starts Here</div>
          </div>
          <div className="journey-text">
            <div className="quote-container">
              <div className="quote-mark">"</div>
              <p className="journey-quote">
                Every journey begins with a single step. <span className="gold">You don't have to walk alone.</span>
              </p>
            </div>
            <div className="quote-decoration"></div>
            <p>
              Recovery is not just about stopping—it's about starting a new chapter 
              filled with purpose, clarity, and support.
            </p>
            <div className="compassion-badge">
              <div className="compassion-icon">
                <Heart size={22} />
              </div>
              <div className="compassion-text">
                <h4>Compassionate Guidance</h4>
                <p>Every step of the way</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="about-header">
          <h2>About <span className="gold">Dr. Drea</span></h2>
          <div className="underline"></div>
          <p>
            A compassionate professional dedicated to helping individuals reclaim their 
            lives through evidence-based coaching and unwavering support.
          </p>
        </div>

        {/* Professional Background Card */}
        <div className="about-card">
          <div className="about-card-header">
            <div className="icon">
              <Award size={24} />
            </div>
            <h3>Professional Background</h3>
          </div>
          <p>
            As a previous drug and alcohol counselor and mental health professional, 
            I've witnessed firsthand the transformative power of dedicated support 
            and personalized recovery strategies.
          </p>
          <p>
            My approach combines professional expertise with genuine compassion, 
            creating a safe space for growth, accountability, and lasting change. 
            Every journey is unique, and I'm here to walk alongside you every step of the way.
          </p>

          <div className="credentials-section">
            <div className="credentials-title">
              <Award size={18} />
              <h4>Education & Credentials</h4>
            </div>
            <div className="credentials-grid">
              <div className="credential-box">
                <div className="credential-icon doctorate">
                  <GraduationCap size={28} />
                </div>
                <h5>Doctorate</h5>
                <p>University of Southern California</p>
              </div>
              <div className="credential-box">
                <div className="credential-icon masters">
                  <GraduationCap size={28} />
                </div>
                <h5>Master's Degree</h5>
                <p>University of Southern California</p>
              </div>
              <div className="credential-box">
                <div className="credential-icon undergrad">
                  <BookOpen size={28} />
                </div>
                <h5>Undergraduate Degree</h5>
                <p>University of California, Santa Cruz</p>
              </div>
              <div className="credential-box">
                <div className="credential-icon certified">
                  <Medal size={28} />
                </div>
                <h5>Certified Sobriety and Life Coach</h5>
                <p>Udemy</p>
              </div>
            </div>
          </div>
        </div>

        {/* Areas of Expertise - New Engaging Layout */}
        <div className="expertise-section">
          <div className="expertise-header">
            <h3>Areas of <span className="gold">Expertise</span></h3>
            <p>Comprehensive support tailored to your unique needs</p>
          </div>
          <div className="expertise-grid">
            <div className="expertise-card" data-testid="expertise-recovery">
              <div className="expertise-icon recovery">
                <Heart size={24} />
              </div>
              <div className="expertise-content">
                <h4>Drug & Alcohol Recovery Coaching</h4>
                <p>Evidence-based strategies for lasting sobriety and relapse prevention</p>
              </div>
            </div>
            <div className="expertise-card" data-testid="expertise-life">
              <div className="expertise-icon life">
                <Compass size={24} />
              </div>
              <div className="expertise-content">
                <h4>Life Coaching</h4>
                <p>Holistic guidance for personal growth, balance, and fulfillment</p>
              </div>
            </div>
            <div className="expertise-card" data-testid="expertise-career">
              <div className="expertise-icon career">
                <Briefcase size={24} />
              </div>
              <div className="expertise-content">
                <h4>Career Transition</h4>
                <p>Navigate professional changes with confidence and clarity</p>
              </div>
            </div>
            <div className="expertise-card" data-testid="expertise-education">
              <div className="expertise-icon education">
                <GraduationCap size={24} />
              </div>
              <div className="expertise-content">
                <h4>College & Career Advising</h4>
                <p>Strategic planning for academic and professional success</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section - Now with 4 cards */}
      <section className="why-section">
        <div className="why-header">
          <h2>Why Choose <span className="gold">Sober Coaching?</span></h2>
          <div className="underline"></div>
        </div>

        <div className="why-grid four-columns">
          <div className="why-card" data-testid="why-card-support">
            <div className="why-card-icon">
              <Heart size={28} />
            </div>
            <h3>Personalized Support</h3>
            <p>
              Tailored coaching strategies designed specifically for your unique 
              recovery journey and lifestyle goals.
            </p>
          </div>

          <div className="why-card" data-testid="why-card-accountability">
            <div className="why-card-icon">
              <Users size={28} />
            </div>
            <h3>Accountability Partner</h3>
            <p>
              Regular check-ins and ongoing support to keep you motivated and 
              on track with your sobriety goals.
            </p>
          </div>

          <div className="why-card" data-testid="why-card-expertise">
            <div className="why-card-icon">
              <Award size={28} />
            </div>
            <h3>Professional Expertise</h3>
            <p>
              Benefit from professional and lived experience in substance abuse 
              and mental health counseling.
            </p>
          </div>

          <div className="why-card" data-testid="why-card-alternative">
            <div className="why-card-icon alternative">
              <Building size={28} />
            </div>
            <h3>Inpatient Treatment Alternative</h3>
            <p>
              Feel like you aren't ready for rehab? Feel like you need more flexibility? 
              Sober coaching can be a great start!
            </p>
          </div>
        </div>
      </section>

      {/* Starting Point Section */}
      <section className="starting-section">
        <div className="starting-card">
          <div className="starting-icon">
            <Zap size={28} />
          </div>
          <div className="starting-content">
            <h3>A Starting Point</h3>
            <p>
              Curious about your relationship with substances? Curious about sobriety? 
              Not sure how to get started? <strong>You've come to the right place.</strong> 1:1 coaching 
              is a great first step in understanding your needs.
            </p>
            <Link to="/contact" className="start-link" data-testid="start-journey-btn">
              Start Your Journey <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2>Ready to Begin Your <span className="gold">Recovery Journey?</span></h2>
        <p>Take the first step towards a healthier, sober life. Explore our coaching packages or reach out today.</p>
        <div className="cta-buttons">
          <Link to="/packages" className="btn-primary" data-testid="explore-packages-btn">
            Explore Packages
          </Link>
          <button 
            type="button"
            className="btn-secondary" 
            data-testid="schedule-consultation-btn"
            onClick={() => window.open('https://calendly.com/drea-drdreasobercoaching/free-consultation-w-dr-drea', '_blank')}
          >
            Schedule Consultation
          </button>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
